/**
 * 独享容器 (Kata) 用户/后台共用小部件：状态标签、规格、用量、电源按钮、日志、端口表。
 * 终端/进程/实时日志见 ContainerTools，文件管理见 ContainerFiles。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Descriptions, Popconfirm, Space, Table, Tag, Tooltip, Typography, message } from 'antd';
import {
    DeleteOutlined, LoadingOutlined, PlayCircleOutlined, PoweroffOutlined, ReloadOutlined, SyncOutlined, WarningOutlined,
} from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import { isOk, errMsg } from '../../services/containerService';

const { Text } = Typography;

export const CONTAINER_STATUS = {
    creating: ['创建中', 'processing'],
    running: ['运行中', 'success'],
    stopped: ['已停止', 'default'],
    suspended: ['已暂停', 'warning'],
    error: ['异常', 'error'],
    deleting: ['删除中', 'default'],
    provisioning: ['开通中', 'processing'], // paid, waiting for a node with room (no container yet)
    processing: ['处理中', 'processing'], // what customers see instead of error (ops gets the alert)
};
export const PENDING = ['creating', 'deleting', 'provisioning'];
export const needsPoll = (c) => !!(c && (c.busy || PENDING.includes(c.status)));

export const ContainerStatusTag = ({ status, busy }) => {
    const { t } = useTranslation();
    const [label, color] = CONTAINER_STATUS[status] || [status || '-', 'default'];
    return (
        <Space size={4}>
            <Tag color={color} style={{ marginInlineEnd: 0 }}>{t(label)}</Tag>
            {busy && <Tooltip title={t('Task running')}><LoadingOutlined style={{ fontSize: 12 }} /></Tooltip>}
        </Space>
    );
};

export const fmtBytes = (b) => {
    const v = Number(b);
    if (!Number.isFinite(v) || v <= 0) return '0 B';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(u.length - 1, Math.floor(Math.log(v) / Math.log(1024)));
    return `${(v / 1024 ** i).toFixed(i ? 1 : 0)} ${u[i]}`;
};
const fmtMem = (mb) => {
    const v = Number(mb) || 0;
    return v >= 1024 ? `${+(v / 1024).toFixed(1)} GB` : `${v} MB`;
};
export const specText = (c) => {
    const cpu = c?.cpus ? +(Number(c.cpus) / 1000).toFixed(2) : '-';
    return `${cpu} vCPU · ${fmtMem(c?.memory_mb)} · ${c?.disk_gb ?? '-'} GB`;
};

export const UsageBlock = ({ c }) => {
    const { t } = useTranslation();
    const u = c?.usage || {};
    const diskLimit = (Number(c?.disk_gb) || 0) * 1024 ** 3;
    return (
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }} bordered>
            <Descriptions.Item label={t('Spec')}>{specText(c)}</Descriptions.Item>
            <Descriptions.Item label={t('Memory')}>{fmtBytes(u.mem_used_bytes)} / {u.mem_limit_bytes ? fmtBytes(u.mem_limit_bytes) : fmtMem(c?.memory_mb)}</Descriptions.Item>
            <Descriptions.Item label={t('Disk')}>{fmtBytes(u.disk_used_bytes)} / {diskLimit ? fmtBytes(diskLimit) : '-'}</Descriptions.Item>
            <Descriptions.Item label={t('Traffic')}>↓ {fmtBytes(u.net_in_bytes)} / ↑ {fmtBytes(u.net_out_bytes)}</Descriptions.Item>
        </Descriptions>
    );
};

const canAct = (c) => c && !c.busy && ['running', 'stopped', 'error'].includes(c.status);

/** 启动/停止/重启/删除。api 需提供 power(id, action) 与 remove(id)。 */
export const PowerButtons = ({ c, api, onDone, onDeleted, size = 'small', type = 'text', showDelete = true }) => {
    const { t } = useTranslation();
    const [acting, setActing] = useState(null);
    const run = async (action) => {
        setActing(action);
        const r = action === 'delete' ? await api.remove(c.id) : await api.power(c.id, action);
        setActing(null);
        if (isOk(r)) {
            message.success(r.msg || t('Submitted'));
            if (action === 'delete' && onDeleted) onDeleted(); else if (onDone) onDone();
        } else {
            message.error(errMsg(r, t('Operation failed')));
        }
    };
    const ok = canAct(c) && !acting;
    const running = c?.state === 'running' || (c?.state !== 'stopped' && c?.status === 'running');
    const btn = (action, icon, title, extra = {}) => (
        <Tooltip title={title}>
            <Button size={size} type={type} icon={icon} loading={acting === action} {...extra} />
        </Tooltip>
    );
    return (
        <Space size={4} onClick={(e) => e.stopPropagation()}>
            {btn('start', <PlayCircleOutlined />, t('Start'), { disabled: !ok || running, onClick: () => run('start') })}
            <Popconfirm title={t('Stop it?')} disabled={!ok || !running} onConfirm={() => run('stop')}>
                {btn('stop', <PoweroffOutlined />, t('Stop'), { disabled: !ok || !running })}
            </Popconfirm>
            <Popconfirm title={t('Restart?')} disabled={!ok || !running} onConfirm={() => run('restart')}>
                {btn('restart', <ReloadOutlined />, t('Restart'), { disabled: !ok || !running })}
            </Popconfirm>
            {showDelete && (
                <Popconfirm title={t('Delete this container? Data cannot be recovered')} okType="danger" okText={t('Delete')}
                    disabled={!!acting || c?.status === 'deleting'} onConfirm={() => run('delete')}>
                    {btn('delete', <DeleteOutlined />, t('Delete'), { danger: true, disabled: !!acting || c?.status === 'deleting' })}
                </Popconfirm>
            )}
        </Space>
    );
};

/** 日志：pending 时每 3 秒重取，最多约 30 秒。fetcher() → {status, data:{text, at, pending}} */
export const LogsPanel = ({ fetcher }) => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const preRef = useRef(null);
    const timer = useRef(null);
    const tries = useRef(0);

    const load = useCallback(async (reset = true) => {
        if (reset) { tries.current = 0; clearTimeout(timer.current); }
        setLoading(true);
        const r = await fetcher();
        setLoading(false);
        if (!isOk(r)) { message.error(errMsg(r, t('Failed to load'))); return; }
        setData(r.data || {});
        if (r.data?.pending && tries.current < 10) {
            tries.current += 1;
            timer.current = setTimeout(() => load(false), 3000);
        }
    }, [fetcher, t]);

    useEffect(() => { load(); return () => clearTimeout(timer.current); }, [load]);
    useEffect(() => { if (preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight; }, [data]);

    const pending = !!data?.pending;
    return (
        <div>
            <Space style={{ marginBottom: 8 }}>
                <Button size="small" icon={<SyncOutlined spin={pending} />} loading={loading && !pending} onClick={() => load()}>{t('Refresh')}</Button>
                {pending && <Text type="secondary">{t('Fetching logs…')}</Text>}
                {data?.at && <Text type="secondary">{new Date(data.at * 1000).toLocaleString()}</Text>}
            </Space>
            <pre ref={preRef} style={{
                margin: 0, padding: 12, maxHeight: 480, minHeight: 160, overflow: 'auto', fontSize: 12,
                fontFamily: 'Menlo, Consolas, monospace', background: '#141414', color: '#ddd', borderRadius: 6,
                whiteSpace: 'pre-wrap', wordBreak: 'break-all',
            }}>{data?.text || (pending ? '' : t('No logs yet'))}</pre>
        </div>
    );
};

export const HealthDot = ({ healthy }) => {
    const { t } = useTranslation();
    if (healthy === true) return <Badge status="success" text={t('Health')} />;
    if (healthy === false) return <Badge status="error" text={t('Error')} />;
    return <Badge status="default" text={t('Unknown')} />;
};

export const arr = (v) => (Array.isArray(v) ? v : []);

/** 端口映射表。onDelete 不传则只读。 */
export const PortTable = ({ ports, publicIp, onDelete }) => {
    const { t } = useTranslation();
    const cols = [
        {
            title: t('Public network'), key: 'ext', render: (_, p) => (
                <Space size={6}>
                    <Text copyable style={{ fontFamily: 'monospace' }}>{p.public || `${publicIp || '-'}:${p.external_port}`}</Text>
                    {p.sg_open === false && (
                        <Tooltip title={t('Security group does not allow this port')}><WarningOutlined style={{ color: '#faad14' }} /></Tooltip>
                    )}
                </Space>
            ),
        },
        { title: t('Container port'), key: 'int', render: (_, p) => `${p.internal_port}/${p.protocol}` },
        { title: t('Status'), dataIndex: 'status', render: (v) => <Tag>{v || '-'}</Tag> },
        ...(onDelete ? [{
            title: '', key: 'op', width: 60, render: (_, p) => (
                <Popconfirm title={t('Delete it?')} onConfirm={() => onDelete(p)}>
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            ),
        }] : []),
    ];
    return <Table size="small" rowKey="id" columns={cols} dataSource={arr(ports)} pagination={false} locale={{ emptyText: t('No port mappings') }} />;
};

